import React from 'react';
import { useTranslation } from 'react-i18next';

import CheckIcon from '@mui/icons-material/Check';
import { ListItemIcon, ListItemText, Typography } from '@mui/material';

import DayIcon from '@common/assets/icons/day.svg';
import WeekIcon from '@common/assets/icons/week.svg';

import {
	StyledGradeBookFormatButton,
	StyledGradeBookFormatMenu,
	StyledGradeBookFormatMenuItem,
	StyledGradeBookFormatWrap,
} from './styled';

interface PropsType {
	format: 'day' | 'week';
	onSetFormat: (format: 'day' | 'week') => void;
}

function GradeBookFormatSwitcher({ format, onSetFormat }: PropsType) {
	const { t } = useTranslation();
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

	const formatOptions = React.useMemo(
		() => [
			{ value: 'day' as const, label: t('shared.day'), Icon: DayIcon },
			{ value: 'week' as const, label: t('shared.week'), Icon: WeekIcon },
		],
		[t],
	);

	const selectedFormat =
		formatOptions.find((option) => option.value === format) ?? formatOptions[0];
	const menuId = 'gradebook-format-menu';

	const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
	};

	const handleSelectFormat = (value: 'day' | 'week') => {
		onSetFormat(value);
		setAnchorEl(null);
	};

	return (
		<StyledGradeBookFormatWrap>
			<StyledGradeBookFormatButton
				isOpen={Boolean(anchorEl)}
				aria-haspopup="true"
				aria-expanded={Boolean(anchorEl)}
				aria-controls={menuId}
				aria-label={selectedFormat.label}
				onClick={handleOpenMenu}
				startIcon={<selectedFormat.Icon />}
			/>
			<StyledGradeBookFormatMenu
				id={menuId}
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleCloseMenu}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				{formatOptions.map(({ value, label, Icon }) => {
					const isSelected = value === format;
					return (
						<StyledGradeBookFormatMenuItem
							key={value}
							selected={isSelected}
							onClick={() => handleSelectFormat(value)}
						>
							<ListItemIcon>
								<Icon />
							</ListItemIcon>
							<ListItemText primary={<Typography variant="body1">{label}</Typography>} />
							{isSelected && (
								<CheckIcon fontSize="small" sx={{ color: 'inherit', marginLeft: 'auto' }} />
							)}
						</StyledGradeBookFormatMenuItem>
					);
				})}
			</StyledGradeBookFormatMenu>
		</StyledGradeBookFormatWrap>
	);
}

export default GradeBookFormatSwitcher;
